// TRUST-REIMBURSEMENTS-2/4 (2026-06-02) — cross-claim Reimbursement Requests +
// accounting deposit-entry workspace. Reports → "Reimbursement Requests".
// Distinct from the legacy "Reimbursement Status Report" (pending_reimbursements).
//
// Rows are scoped SERVER-SIDE by GET /reimbursements (_claims_visibility): a line
// adjuster sees only their claims' requests; back-office sees all; the IA/TPA wall
// always applies. Recording a deposit is gated by approve_reimbursement
// (accountant + admin) here AND enforced server-side on POST receipts.
import { useState, useEffect, useCallback, Fragment } from "react";
import { apiFetch, fmt, canOnClaim, getUser } from "../api";
import { TYPE_LABEL, StatusPill, downloadReimbExport } from "./Reimbursements";

// Status filter options; "outstanding" is the accounting work-queue (Sent + Partial).
const STATUS_OPTS = [
  ["", "All statuses"],
  ["outstanding", "Outstanding (Sent + Partial)"],
  ["Draft", "Draft"], ["Sent", "Sent"], ["Partially Received", "Partially Received"],
  ["Received", "Received"], ["Closed", "Closed"], ["Void", "Void"],
];

function Metric({ label, value, color }) {
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "JetBrains Mono,monospace", marginTop: 1, color: color || "inherit" }}>{value}</div>
    </div>
  );
}

const COLS = [
  { key: "claim_number", label: "Claim #" },
  { key: "insured_name", label: "Insured" },
  { key: "lead_adjuster_name", label: "Lead Adjuster" },
  { key: "request_type", label: "Type" },
  { key: "amount_requested", label: "Requested", num: true, right: true },
  { key: "received_total", label: "Received", num: true, right: true },
  { key: "outstanding_total", label: "Outstanding", num: true, right: true },
  { key: "age_days", label: "Age", num: true, right: true },
  { key: "status", label: "Status" },
  { key: "request_date", label: "Date" },
];

export default function ReimbursementRequestsReport({ onSelectClaim, initialViewState, onViewStateChange }) {
  const iv = initialViewState || {};   // REPORTS-UX-1: restore a saved/pinned view
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(iv.status ?? "outstanding"); // default to the work queue
  const [q, setQ] = useState(iv.q ?? "");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sort, setSort] = useState(iv.sort ?? { key: "age_days", dir: "desc" }); // oldest first
  const [depositFor, setDepositFor] = useState(null); // request id with an open deposit form
  // REPORTS-UX-1: emit serialized view state up for pin/recent/saved-view capture.
  useEffect(() => {
    onViewStateChange && onViewStateChange({ status, q, sort });
    /* eslint-disable-next-line */
  }, [status, q, sort]);

  const canDeposit = canOnClaim("approve_reimbursement", { lead_adjuster_id: null, claim_staff_user_ids: [] }, getUser());

  const load = useCallback(() => {
    setLoading(true); setDepositFor(null);
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q.trim()) params.set("q", q.trim());
    const qs = params.toString();
    apiFetch(`/reimbursements${qs ? "?" + qs : ""}`)
      .then(d => { setRows(d.requests || []); setSummary(d.summary || null); setErr(null); })
      .catch(e => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [status, q]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const toggleSort = (key) => setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const sorted = [...rows].sort((a, b) => {
    const col = COLS.find(c => c.key === sort.key) || {};
    let av = a[sort.key], bv = b[sort.key];
    if (sort.key === "request_date") { av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0; }
    else if (col.num) { av = av == null ? -Infinity : Number(av); bv = bv == null ? -Infinity : Number(bv); }
    else { av = (av || "").toString().toLowerCase(); bv = (bv || "").toString().toLowerCase(); }
    if (av < bv) return sort.dir === "asc" ? -1 : 1;
    if (av > bv) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const th = { padding: "8px 10px", textAlign: "left", fontSize: 11, color: "var(--text3)", textTransform: "uppercase", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" };
  const td = { padding: "8px 10px", fontSize: 13, borderTop: "1px solid var(--border)" };
  const mono = { fontFamily: "JetBrains Mono,monospace" };
  const s = summary || {};
  const exportQS = () => { const p = new URLSearchParams(); if (status) p.set("status", status); if (q.trim()) p.set("q", q.trim()); const x = p.toString(); return x ? "?" + x : ""; };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 4, background: "white" }}>
          {STATUS_OPTS.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === "Enter") load(); }}
          placeholder="Search claim # or insured…"
          style={{ padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 4, minWidth: 240 }} />
        <button className="btn btn-blue btn-sm" onClick={load}>Run</button>
        <button className="btn btn-outline btn-sm" disabled={loading || rows.length === 0}
          onClick={() => downloadReimbExport(`/reports/reimbursement-requests/export${exportQS()}`).catch(e => setErr(String(e.message || e)))}>⤓ Export XLSX</button>
        <div style={{ flex: 1 }} />
        {s && <div style={{ display: "flex", gap: 20 }}>
          <Metric label="Requested" value={fmt.money(s.total_requested || 0)} />
          <Metric label="Received" value={fmt.money(s.total_received || 0)} color="#166534" />
          <Metric label="Outstanding" value={fmt.money(s.total_outstanding || 0)} color={(s.total_outstanding || 0) > 0 ? "var(--navy)" : "inherit"} />
          <Metric label="Open" value={s.open_count || 0} />
        </div>}
      </div>

      {err && <div style={{ color: "var(--red)", fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {loading ? <div className="loading">Generating report…</div>
        : rows.length === 0 ? <div className="empty-state">No reimbursement requests match — you see requests on claims you can access.</div>
        : (
          <table style={{ width: "100%" }}>
            <thead>
              <tr>
                {COLS.map(c => (
                  <th key={c.key} style={{ ...th, textAlign: c.right ? "right" : "left" }} onClick={() => toggleSort(c.key)}>
                    {c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
                {canDeposit && <th style={{ ...th, cursor: "default", textAlign: "right" }}></th>}
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
                const outstanding = (r.outstanding_total || 0) > 0 && r.status !== "Void";
                const old = r.age_days != null && r.age_days >= 30 && outstanding;
                return (
                  <Fragment key={r.id}>
                    <tr>
                      <td style={td}>
                        {onSelectClaim
                          ? <span style={{ color: "var(--navy)", cursor: "pointer", fontWeight: 600 }} onClick={() => onSelectClaim(r.claim_id)}>{r.claim_number}</span>
                          : r.claim_number}
                        {r.matter_type === "tpa" && <span className="badge" style={{ marginLeft: 6, fontSize: 9, background: "#ede9fe", color: "#5b21b6" }}>TPA</span>}
                      </td>
                      <td style={td}>{r.insured_name || "—"}</td>
                      <td style={td}>{r.lead_adjuster_name || "—"}</td>
                      <td style={td}>{TYPE_LABEL[r.request_type] || r.request_type}</td>
                      <td style={{ ...td, ...mono, textAlign: "right" }}>{fmt.money(r.amount_requested)}</td>
                      <td style={{ ...td, ...mono, textAlign: "right", color: (r.received_total || 0) > 0 ? "#166534" : "var(--text3)" }}>{fmt.money(r.received_total)}</td>
                      <td style={{ ...td, ...mono, textAlign: "right", color: outstanding ? "var(--navy)" : "var(--text3)" }}>{fmt.money(r.outstanding_total)}</td>
                      <td style={{ ...td, ...mono, textAlign: "right", color: old ? "#b45309" : "var(--text3)", fontWeight: old ? 700 : 400 }}>
                        {r.age_days == null ? "—" : `${r.age_days}d`}{old ? " ⚠" : ""}
                      </td>
                      <td style={td}><StatusPill status={r.status} /></td>
                      <td style={{ ...td, color: "var(--text3)", whiteSpace: "nowrap" }}>{fmt.date(r.request_date)}</td>
                      {canDeposit && (
                        <td style={{ ...td, textAlign: "right" }}>
                          {(r.status === "Sent" || r.status === "Partially Received") &&
                            <button className="btn btn-blue btn-sm" style={{ padding: "2px 8px", fontSize: 11 }}
                              onClick={() => setDepositFor(depositFor === r.id ? null : r.id)}>
                              {depositFor === r.id ? "Cancel" : "Record Deposit"}
                            </button>}
                        </td>
                      )}
                    </tr>
                    {canDeposit && depositFor === r.id && (
                      <tr>
                        <td colSpan={COLS.length + 1} style={{ padding: 0, borderTop: "1px solid var(--border)", background: "var(--bg2,#f8fafc)" }}>
                          <DepositForm request={r} onClose={() => setDepositFor(null)} onSaved={() => load()} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
    </div>
  );
}

// Inline deposit-entry form: records a receipt against a request from the
// accounting list, with a prominent slip-holder selector so the deposit is
// explicitly credited to an underwriter (or the whole request).
function DepositForm({ request, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [method, setMethod] = useState("wire");
  const [reference, setReference] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    apiFetch(`/reimbursements/${request.id}`).then(setDetail).catch(e => setErr(String(e.message || e)));
  }, [request.id]);

  const save = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setErr("Enter a deposit amount greater than 0."); return; }
    setSaving(true); setErr(null);
    apiFetch(`/reimbursements/${request.id}/receipts`, {
      method: "POST",
      body: { amount: amt, received_date: date || null, method, reference, recipient_id: recipientId ? Number(recipientId) : null },
    }).then(() => onSaved()).catch(e => setErr(String(e.message || e))).finally(() => setSaving(false));
  };

  const lbl = { fontSize: 11, color: "var(--text2)", fontWeight: 600, display: "block", marginBottom: 3 };
  const inp = { width: "100%", padding: "6px 9px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6 };
  const recips = detail?.recipients || [];
  return (
    <div style={{ padding: "14px 16px", display: "grid", gap: 10 }}>
      <div style={{ fontWeight: 600, fontSize: 13 }}>Record deposit — {request.claim_number} · {fmt.money(request.outstanding_total)} outstanding</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        <div><label style={lbl}>Amount received</label><input style={inp} type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" /></div>
        <div><label style={lbl}>Date received</label><input style={inp} type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
        <div><label style={lbl}>Method</label>
          <select style={inp} value={method} onChange={e => setMethod(e.target.value)}>
            <option value="wire">Wire</option><option value="check">Check</option><option value="ach">ACH</option><option value="other">Other</option>
          </select>
        </div>
        <div><label style={lbl}>Reference (check # / wire ref)</label><input style={inp} value={reference} onChange={e => setReference(e.target.value)} /></div>
      </div>
      <div>
        <label style={lbl}>Credit to slip-holder</label>
        <select style={{ ...inp, fontWeight: recipientId ? 600 : 400 }} value={recipientId} onChange={e => setRecipientId(e.target.value)}>
          <option value="">Whole request (not credited to a specific underwriter)</option>
          {recips.map(rc => (
            <option key={rc.id} value={rc.id}>
              {rc.display_name} — {rc.participation_pct != null ? Number(rc.participation_pct).toFixed(2) + "%" : "—"} · {fmt.money(Number(rc.allocated_amount || 0) - Number(rc.received_amount || 0))} outstanding
            </option>
          ))}
        </select>
        {recips.length > 0 && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 3 }}>Crediting a slip-holder updates that underwriter's Received / Outstanding on the statement.</div>}
      </div>
      {err && <div style={{ color: "var(--red)", fontSize: 12 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !detail}>{saving ? "Saving…" : "Record Deposit"}</button>
      </div>
    </div>
  );
}
